import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, Clock, MapPin, User, ArrowLeft } from 'lucide-react';
import { api, type ApiError } from '../../lib/api';
import { type Slot, type SlotsResponse, browserTz, formatSlotLocal } from '../../lib/slots';
import SlotPicker from '../../components/SlotPicker';

type ManageState = 'active' | 'cancelled' | 'closed' | 'past';

type ManageSummary = {
  start_time: string;
  end_time: string;
  duration_minutes: number;
  timezone: string;
  status: string;
  bookee_name: string;
  meeting_link: string | null;
  org_slug: string;
  brand_name: string;
  staff_slug: string;
  staff_name: string;
  location_slug: string;
  location_name: string;
  location_address: string;
  service_slug: string | null;
  service_name: string;
  state: ManageState;
  can_cancel: boolean;
  can_reschedule: boolean;
};

// 2 weeks of availability is plenty for a reschedule; the slots endpoint caps
// the window at 31 days and floors `from` to now.
const RESCHEDULE_WINDOW_DAYS = 14;

export default function ManageBooking() {
  const { token } = useParams();
  const tz = useMemo(browserTz, []);

  const [summary, setSummary] = useState<ManageSummary | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  // 'view' = summary + action buttons; 'reschedule' = slot picker.
  const [mode, setMode] = useState<'view' | 'reschedule'>('view');
  // Terminal states after a successful action.
  const [done, setDone] = useState<{ kind: 'cancelled' } | { kind: 'rescheduled'; start: string } | null>(
    null,
  );

  // Cancel form
  const [reason, setReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Reschedule picker
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [slotsErr, setSlotsErr] = useState<string | null>(null);
  const [picked, setPicked] = useState<Slot | null>(null);
  const [moving, setMoving] = useState(false);

  const [actionErr, setActionErr] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api<ManageSummary>(`/book/manage/${token}`)
      .then((s) => {
        if (!cancelled) setSummary(s);
      })
      .catch((e) => {
        if (cancelled) return;
        const status = (e as Error & { status?: number }).status;
        setLoadErr(status === 404 ? 'not_found' : 'load_failed');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Fetch slots when the user opens the reschedule picker.
  useEffect(() => {
    if (mode !== 'reschedule' || !summary || !summary.service_slug) return;
    let cancelled = false;
    setSlots(null);
    setSlotsErr(null);
    const from = new Date().toISOString();
    const to = new Date(Date.now() + RESCHEDULE_WINDOW_DAYS * 86_400_000).toISOString();
    const qs = new URLSearchParams({
      service: summary.service_slug,
      location: summary.location_slug,
      from,
      to,
    });
    api<SlotsResponse>(`/book/${summary.org_slug}/staff/${summary.staff_slug}/slots?${qs}`)
      .then((r) => {
        if (cancelled) return;
        setSlots(r.slots);
      })
      .catch((e) => {
        if (cancelled) return;
        setSlotsErr((e as ApiError)?.error ?? 'slots_failed');
      });
    return () => {
      cancelled = true;
    };
  }, [mode, summary, tz]);

  async function confirmCancel() {
    if (!token) return;
    setCancelling(true);
    setActionErr(null);
    try {
      await api(`/book/manage/${token}/cancel`, {
        method: 'POST',
        body: { reason: reason.trim() || undefined },
      });
      setDone({ kind: 'cancelled' });
    } catch (e) {
      setActionErr((e as ApiError)?.error ?? 'cancel_failed');
    } finally {
      setCancelling(false);
    }
  }

  async function confirmReschedule() {
    if (!token || !picked) return;
    setMoving(true);
    setActionErr(null);
    try {
      await api(`/book/manage/${token}/reschedule`, {
        method: 'POST',
        body: { start_time: picked.startUtc, timezone: tz },
      });
      setDone({ kind: 'rescheduled', start: picked.startUtc });
    } catch (e) {
      // 409 reasons: slot_taken / outside_hours / past_slot, etc.
      setActionErr((e as ApiError)?.error ?? 'reschedule_failed');
    } finally {
      setMoving(false);
    }
  }

  // ---- Render ----

  if (loadErr) {
    return (
      <div className="card" style={{ maxWidth: 560, margin: '2rem auto' }}>
        <h2>Booking link unavailable</h2>
        <p className="muted">
          {loadErr === 'not_found'
            ? 'This link is invalid or has expired. If you still need to make a change, reply to your confirmation email and we’ll help.'
            : 'Something went wrong loading your booking. Please try again in a moment.'}
        </p>
      </div>
    );
  }

  if (!summary) {
    return <p className="muted" style={{ margin: '2rem auto', maxWidth: 560 }}>Loading your booking…</p>;
  }

  if (done) {
    return (
      <div className="card" style={{ maxWidth: 560, margin: '2rem auto' }}>
        <h2>{done.kind === 'cancelled' ? 'Booking cancelled' : 'Booking rescheduled'}</h2>
        <p className="muted">
          {done.kind === 'cancelled'
            ? `Your ${summary.service_name} with ${summary.staff_name} has been cancelled. A confirmation has been sent to you.`
            : `Your ${summary.service_name} with ${summary.staff_name} is now booked for ${formatSlotLocal(done.start, tz)}. A confirmation has been sent to you.`}
        </p>
      </div>
    );
  }

  const summaryCard = (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div className="muted small" style={{ marginBottom: 4 }}>{summary.brand_name}</div>
      <h2 style={{ marginTop: 0 }}>{summary.service_name}</h2>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
        <li><User size={14} /> {summary.staff_name}</li>
        <li><Calendar size={14} /> {formatSlotLocal(summary.start_time, tz)}</li>
        <li><Clock size={14} /> {summary.duration_minutes} min</li>
        <li>
          <MapPin size={14} />{' '}
          {summary.meeting_link ? (
            <a href={summary.meeting_link} target="_blank" rel="noopener noreferrer">Online meeting</a>
          ) : (
            `${summary.location_name} — ${summary.location_address}`
          )}
        </li>
      </ul>
    </div>
  );

  // Not actionable (already cancelled / past / closed).
  if (!summary.can_cancel && !summary.can_reschedule) {
    return (
      <div style={{ maxWidth: 560, margin: '2rem auto' }}>
        {summaryCard}
        <p className="muted">
          {summary.state === 'cancelled'
            ? 'This booking has already been cancelled.'
            : 'This booking can no longer be changed online. Reply to your confirmation email if you need help.'}
        </p>
      </div>
    );
  }

  if (mode === 'reschedule') {
    return (
      <div style={{ maxWidth: 560, margin: '2rem auto' }}>
        <button className="link" onClick={() => { setMode('view'); setPicked(null); setActionErr(null); }} style={{ marginBottom: 8 }}>
          <ArrowLeft size={14} /> Back
        </button>
        {summaryCard}
        <h3>Pick a new time</h3>
        {slotsErr && <p className="muted">Couldn’t load available times ({slotsErr}). Please try again.</p>}
        {!slots && !slotsErr && <p className="muted">Loading available times…</p>}
        {slots && <SlotPicker slots={slots} tz={tz} picked={picked} onPick={setPicked} />}
        {actionErr && <p className="muted" style={{ marginTop: 12 }}>Couldn’t reschedule ({actionErr}). The slot may have just been taken — pick another.</p>}
        <button
          className="primary"
          disabled={!picked || moving}
          onClick={confirmReschedule}
          style={{ marginTop: 16 }}
        >
          {moving ? 'Rescheduling…' : picked ? `Move to ${formatSlotLocal(picked.startUtc, tz)}` : 'Select a time'}
        </button>
      </div>
    );
  }

  // mode === 'view'
  return (
    <div style={{ maxWidth: 560, margin: '2rem auto' }}>
      {summaryCard}
      <h3>Need to make a change?</h3>
      <div style={{ display: 'grid', gap: 12 }}>
        {summary.can_reschedule && (
          <div>
            <button onClick={() => setMode('reschedule')}>Reschedule</button>
          </div>
        )}
        <div>
          <label className="muted small" htmlFor="cancel-reason">Cancelling? Let us know why (optional)</label>
          <textarea
            id="cancel-reason"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            placeholder="e.g. Something came up"
            style={{ width: '100%', fontFamily: 'inherit', marginTop: 4 }}
          />
          {actionErr && <p className="muted">Couldn’t cancel ({actionErr}). Please try again.</p>}
          <button className="danger" disabled={cancelling} onClick={confirmCancel}>
            {cancelling ? 'Cancelling…' : 'Cancel booking'}
          </button>
        </div>
      </div>
    </div>
  );
}
